import { useEffect, useRef, useState } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import { Loader2, Paperclip, X, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { personMatch } from "../lib/searchText";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { date: today(), amount: "", vat: "", category: "", payment: "cash", vendor: "", invoice_no: "", description: "", employee_id: "", files: [] };
const L = ({ children }) => <label className="mb-1 block text-xs font-semibold text-slate-600">{children}</label>;
const sel = "h-10 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm";

const RiderPick = ({ value, onChange, employees }) => {
  const [q, setQ] = useState("");
  const list = employees.filter((e) => isActiveEmp(e) && personMatch(e, q));
  return (
    <div className="space-y-1">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم المندوب" className="h-8 text-xs" data-testid="vexp-rider-search" />
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={sel} data-testid="vexp-rider">
        <option value="">— بدون ربط: يُقسم على عدد المناديب المقيمين —</option>
        {list.map((e) => <option key={e.id} value={e.id}>{e.name}{e.rider_id ? ` (${e.rider_id})` : ""}</option>)}
      </select>
    </div>
  );
};

export const VarExpenseDialog = ({ open, onOpenChange, initial, categories, employees, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false), [up, setUp] = useState(false), [reading, setReading] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial, employee_id: initial.employee_id || "" } : { ...EMPTY, date: today(), category: categories[0] || "أخرى" }); }, [open, initial, categories]);
  const s = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upload = async (files) => {
    const arr = Array.from(files || []); if (!arr.length) return; setUp(true);
    let done = [];
    try { for (const file of arr) done.push(await api.varExpenseUpload(file)); setF((p) => ({ ...p, files: [...p.files, ...done] })); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل رفع الملف"); } finally { setUp(false); }
    if (done[0] && !Number(f.amount)) readAi(done[0].id);
  };
  const readAi = async (fid) => {
    setReading(true);
    try {
      const r = await api.readVarExpenseInvoice(fid);
      setF((p) => ({ ...p, amount: r.amount ?? p.amount, vat: r.vat || p.vat, vendor: r.vendor || p.vendor, invoice_no: r.invoice_no || p.invoice_no,
        date: r.date || p.date, category: r.category || p.category }));
      toast.success("تمت قراءة الفاتورة — راجع البيانات قبل الحفظ");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت قراءة الفاتورة"); } finally { setReading(false); }
  };
  const vat15 = () => s("vat", Math.round(Number(f.amount || 0) * 15) / 100);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, amount: Number(f.amount), vat: Number(f.vat || 0), employee_id: f.employee_id || null };
      const r = initial ? await api.updateVarExpense(initial.id, body) : await api.createVarExpense(body);
      if (r.status === "pending") toast.success("تم الحفظ — بانتظار اعتماد المدير");
      else if (r.journal_error) toast.warning(`حُفظ المصروف لكن لم يُرحّل القيد: ${r.journal_error}`);
      else toast.success(r.journal_number ? `تم الحفظ وإنشاء القيد ${r.journal_number}` : "تم حفظ المصروف");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const total = Number(f.amount || 0) + Number(f.vat || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} dir="rtl" className="w-[95vw] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="vexp-dialog">
        <DialogHeader className="text-start"><DialogTitle>{initial ? "تعديل مصروف" : "مصروف متغير جديد"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><L>التاريخ</L><Input type="date" value={f.date} onChange={(e) => s("date", e.target.value)} data-testid="vexp-date" /></div>
          <div><L>التصنيف</L>
            <select value={f.category} onChange={(e) => s("category", e.target.value)} className={sel} data-testid="vexp-category">
              {[...new Set([...categories, f.category].filter(Boolean))].map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><L>المبلغ قبل الضريبة (ر.س)</L><Input type="number" value={f.amount} onChange={(e) => s("amount", e.target.value)} data-testid="vexp-amount" /></div>
          <div><L>ضريبة القيمة المضافة</L>
            <div className="flex gap-1"><Input type="number" value={f.vat} onChange={(e) => s("vat", e.target.value)} data-testid="vexp-vat" />
              <Button type="button" variant="outline" size="sm" className="h-10 shrink-0" onClick={vat15} data-testid="vexp-vat15">15%</Button></div></div>
          <div><L>طريقة الدفع</L>
            <select value={f.payment} onChange={(e) => s("payment", e.target.value)} className={sel} data-testid="vexp-payment">
              <option value="cash">نقدي (الصندوق)</option><option value="bank">تحويل بنكي</option><option value="card">بطاقة</option>
            </select></div>
          <div><L>المورد / الجهة</L><Input value={f.vendor} onChange={(e) => s("vendor", e.target.value)} data-testid="vexp-vendor" /></div>
          <div><L>رقم الفاتورة</L><Input value={f.invoice_no} onChange={(e) => s("invoice_no", e.target.value)} data-testid="vexp-invoice-no" /></div>
          <div className="rounded-lg bg-slate-50 p-2 text-center"><L>الإجمالي</L><p className="text-lg font-bold" data-testid="vexp-total">{total.toFixed(2)} ر.س</p></div>
          <div className="sm:col-span-2"><L>الوصف</L><Input value={f.description} onChange={(e) => s("description", e.target.value)} data-testid="vexp-description" /></div>
          <div className="sm:col-span-2"><L>ربط بمندوب (يُحسب تكلفة عليه وحده في تقرير الربح، وبدون ربط يُقسم على كل المقيمين)</L><RiderPick value={f.employee_id} onChange={(v) => s("employee_id", v)} employees={employees} /></div>
          <div className="sm:col-span-2">
            <L>صور / ملفات الفاتورة</L>
            <input ref={ref} type="file" multiple accept="image/*,application/pdf" hidden onChange={(e) => { const fl = Array.from(e.target.files || []); e.target.value = ""; upload(fl); }} data-testid="vexp-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={up} data-testid="vexp-file-btn">{up ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} إرفاق فاتورة</Button>
            {reading && <p className="mt-2 flex items-center gap-1.5 text-xs text-violet-700" data-testid="vexp-reading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> جاري قراءة الفاتورة بالذكاء الاصطناعي…</p>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {f.files.map((x, i) => (
                <span key={x.id} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700" data-testid={`vexp-file-${i}`}>
                  {x.name}<button type="button" title="قراءة بالذكاء الاصطناعي" disabled={reading} onClick={() => readAi(x.id)} data-testid={`vexp-file-read-${i}`}><Sparkles className="h-3 w-3 text-violet-600" /></button><button type="button" onClick={() => s("files", f.files.filter((_, j) => j !== i))} data-testid={`vexp-file-remove-${i}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={busy || reading || !(Number(f.amount) > 0) || !f.date} data-testid="vexp-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ</Button>
      </DialogContent>
    </Dialog>
  );
};
